import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types/product';
import cn from 'classnames';
import './ProductCard.scss';

interface ProductCardProps {
    product: Product;
    className?: string;
    onToggleFavorite: (productId: string) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
                                                            product,
                                                            className,
                                                            onToggleFavorite
                                                        }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/product/${product.id}`);
    };

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleFavorite(product.id);
    };

    const renderPrice = () => {
        if (product.purchased) return null;
        return (
            <div className="product-card__price-container">
                {product.discount ? (
                    <>
                        <div className="product-card__discount-wrapper">
                            <span className="product-card__discount-price">
                                {product.discount.toLocaleString('ru-RU')} ₽
                            </span>
                            <span className="product-card__original-price">
                                {product.price.toLocaleString('ru-RU')} ₽
                            </span>
                        </div>
                        {/*<span className="product-card__video-amount">({product.videosCount} видео)</span>*/}
                    </>
                ) : (
                    <>
                        <span className="product-card__regular-price">
                        {product.price === 0 ? 'Бесплатно' : `${product.price} ₽`}
                        </span>
                        {/*<span className="product-card__video-amount-regular-price">({product.videosCount} видео)</span>*/}

                    </>
                )}
            </div>
        );
    };
    return (
        <div
            className={cn('product-card', className, {
                'product-card--has-discount': product.discount && !product.purchased
            })}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={`Перейти к продукту ${product.title}`}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        >
            <div className="product-card__image-container">
                <img
                    src={product.image || '/placeholder-image.jpg'}
                    alt={product.title}
                    className="product-card__image"
                    loading="lazy"
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                    }}
                />
                {product.discount && !product.purchased && (
                    <div className="product-card__discount-badge">
                        <svg width="87" height="32" viewBox="0 0 87 32" fill="none">
                            <path
                                d="M0.5 12C0.5 5.37258 5.87258 0 12.5 0H74.5C81.1274 0 86.5 5.37258 86.5 12V32H0.5V12Z"
                                fill="#FB034F"/>
                            <path
                                d="M22.18 17.704H24.756C23.828 19.864 21.732 21.208 19.3 21.208C15.844 21.208 13.076 18.472 13.076 15.048C13.076 11.704 15.812 8.968 19.156 8.968C21.668 8.968 23.732 10.296 24.756 12.568H22.308C21.508 11.528 20.58 11.08 19.204 11.08C16.964 11.08 15.284 12.792 15.284 15.064C15.284 17.288 17.076 19.096 19.252 19.096C20.388 19.096 21.204 18.712 22.18 17.704ZM26.1921 21V12.136H28.3201V16.216L31.3921 12.136H33.9681L30.4321 16.536L34.1601 21H31.3921L28.3201 16.92V21H26.1921ZM35.0515 21V12.136H37.1635V17.992L41.1315 12.136H43.2915V21H41.1795V15.144L37.2115 21H35.0515ZM44.3163 23.112V19.064H45.6763L48.6363 12.136H50.4923L53.4523 19.064H54.8123V23.112H53.2923L52.8443 21H46.3003L45.8523 23.112H44.3163ZM47.8843 19.064H51.2443L49.5643 15.064L47.8843 19.064ZM55.8328 21V12.136H57.9608V16.216L61.0328 12.136H63.6088L60.0728 16.536L63.8008 21H61.0328L57.9608 16.92V21H55.8328ZM64.2761 16.6C64.2761 13.896 66.1641 11.928 68.7881 11.928C70.0681 11.928 70.9481 12.344 71.6841 13.32V12.136H73.6361V21H71.6841V19.848C70.8361 20.824 70.0041 21.208 68.7401 21.208C66.1481 21.208 64.2761 19.272 64.2761 16.6ZM66.4201 16.648C66.4201 18.2 67.4921 19.256 69.0441 19.256C70.5801 19.256 71.6201 18.184 71.6201 16.6C71.6201 15 70.5321 13.88 68.9801 13.88C67.4921 13.88 66.4201 15.048 66.4201 16.648Z"
                                fill="white"/>
                        </svg>
                    </div>
                )}
                <button
                    className={cn('product-card__favorite', {
                        'product-card__favorite--active': product.isFavorite
                    })}
                    onClick={handleFavoriteClick}
                    aria-label={product.isFavorite ? "Удалить из избранного" : "Добавить в избранное"}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill={product.isFavorite ? "#FB034F" : "none"}
                        stroke={product.isFavorite ? "#FB034F" : "#1B1D1D"}
                        strokeWidth="2"
                    >
                        <path
                            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                </button>
            </div>
            {product.purchased ? (
                <div className="product-card__purchased">
                    <div className="purchased-badge">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#00CCC6"
                            strokeWidth="3"
                        >
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <span>Приобретено</span>
                </div>
            ) : (
                renderPrice()
            )}
            <h3 className="product-card__title">{product.title}</h3>
            <div className="product-card__rating">
                <span className="product-card__rating-star">★</span>
                <span className="product-card__rating-value">{product.rating}</span>
                <span className="product-card__reviews">
                    {product.reviewsCount === 0 ? "Нет отзывов"
                        : `${product.reviewsCount} ${getReviewsWord(product.reviewsCount)}`
                    }
                </span>
            </div>
        </div>
    );
};

function getReviewsWord(count: number): string {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'отзывов';
    if (lastDigit === 1) return 'отзыв';
    if (lastDigit >= 2 && lastDigit <= 4) return 'отзыва';
    return 'отзывов';
}